import type {ReactElement, ReactNode} from 'react';
import React, {useContext, useEffect, useState} from 'react';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import {EventWizardDetails} from '@Components/event-wizard/event-wizard-details';
import {Pill, PILL_BACKGROUND_COLOR, PILL_CONTENT_TYPE, PILL_SIZE} from '@Components/pill';
import {ControlledAccordion} from '@Components/controlled-accordion';
import {EventWizardMedia} from '@Components/event-wizard/event-wizard-media';
import {EventWizardRegistrations} from '@Components/event-wizard/event-wizard-registrations';
import {EventWizardOtherOptions} from '@Components/event-wizard/event-wizard-other-options';
import {EventWizardOrganisers} from '@Components/event-wizard/event-wizard-organisers';
import {capitalizeFirstLetter} from '@Utils/string.util';
import {
  useEventWizardCoverPhoto,
  useEventWizardDescription,
  useEventWizardEndDateTime,
  useEventWizardOrganisers,
  useEventWizardRegistrationDetails,
  useEventWizardShouldExpandReviewSection,
  useEventWizardStartDateTime,
  useEventWizardTimeZone,
  useEventWizardTitle,
  useEventWizardVenue,
} from '@Components/event-wizard/event-wizard.hooks';
import {isBasicSectionComplete, isMediaSectionComplete, isOrganiserSectionComplete} from '@Components/event-wizard/event-wizard-review/event-wizard-review.helper';
import {SectionCompleteType, SectionType} from '@Components/event-wizard/event-wizard-review/event-wizard-review.types';
import {toggleDisplayPreviewRegistrationForm, updatePreviewOrganiserVisibilityToShow, updatePreviewRegistrationForm} from '@Components/event-wizard/preview/preview.helper';
import {useDispatch} from 'react-redux';
import {onEventWizardUpdateShouldExpandReviewSection} from '@Components/event-wizard/event-wizard-slice';
import {EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT} from '@Components/event-wizard/event-wizard-container-queries-context';
import {Text, TextSize} from '@Components/text';
import styles from './event-wizard-review.module.scss';

export function EventWizardReview(): ReactElement {
  const dispatch = useDispatch();

  const [openAccordInd, setOpenAccordInd] = useState<SectionType | undefined>(SectionType.MEDIA);

  const baseAccordionParams = {
    className: 'accordion-container spacing-m-b-8 _full-width',
    headerClassName: `${styles.accordionHeader} align-items-start`,
    iconClassName: '_hidden',
    removeHeaderHoverStateWhenOpen: true,
    doHideOnClose: true,
  };

  const title = useEventWizardTitle();
  const venue = useEventWizardVenue();
  const startDateTime = useEventWizardStartDateTime();
  const endDateTime = useEventWizardEndDateTime();
  const timezone = useEventWizardTimeZone();
  const coverPhoto = useEventWizardCoverPhoto();
  const description = useEventWizardDescription();
  const organisers = useEventWizardOrganisers();
  const shouldExpandReviewSection = useEventWizardShouldExpandReviewSection();
  const registrationForm = useEventWizardRegistrationDetails();
  const {isSmallScreen} = useContext(EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT);

  useEffect(() => {
    updateAccordionsByErrState();
  }, [shouldExpandReviewSection]);

  const updateAccordionsByErrState = (): void => {
    if (!shouldExpandReviewSection) {
      return;
    }
    let sectionToOpen: SectionType | undefined = undefined;

    if (!isBasicSectionComplete(title, venue, startDateTime, endDateTime, timezone)) {
      sectionToOpen = SectionType.BASIC;
    } else if (!isMediaSectionComplete(description, coverPhoto)) {
      sectionToOpen = SectionType.MEDIA;
    } else if (!isOrganiserSectionComplete(organisers.selectedOrganiser)) {
      sectionToOpen = SectionType.ORGANISER;
    }

    if (sectionToOpen !== undefined) {
      updateExpandedSection(true, sectionToOpen);
    }
    dispatch(onEventWizardUpdateShouldExpandReviewSection(false));
  };

  const updateExpandedSection = (isOpen: boolean, sectionType: SectionType): void => {
    handleCustomEventForRegistrationSection(isOpen, sectionType);
    handleCustomEventForOrganiserSection(isOpen, sectionType);
    setOpenAccordInd(isOpen ? sectionType : undefined);
  };

  const handleCustomEventForRegistrationSection = (isOpen: boolean, sectionType: SectionType): void => {
    const doOpenRegistrationSection = isOpen && sectionType === SectionType.REGISTRATION;
    toggleDisplayPreviewRegistrationForm(doOpenRegistrationSection);

    if (doOpenRegistrationSection) {
      updatePreviewRegistrationForm(registrationForm, title);
    }
  };

  const handleCustomEventForOrganiserSection = (isOpen: boolean, sectionType: SectionType): void => {
    if (isOpen && sectionType === SectionType.ORGANISER) {
      updatePreviewOrganiserVisibilityToShow();
    }
  };

  const getDonePill = (): ReactElement => {
    return (
      <div className={`${styles.completionPill} radius-round bg-success-10 flex-row-align-center spacing-p-l-2 spacing-p-r-2`}>
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
          <g clipPath="url(#clip0_10067_5543)">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6.99982 0.919006C3.64157 0.919006 0.919006 3.64157 0.919006 6.99982C0.919006 10.3581 3.64157 13.0806 6.99982 13.0806C10.3581 13.0806 13.0806 10.3581 13.0806 6.99982C13.0806 3.64157 10.3581 0.919006 6.99982 0.919006ZM9.89239 5.60732C10.0938 5.4064 10.0938 5.08003 9.89239 4.87857C9.69146 4.67711 9.36456 4.67711 9.1631 4.87857L6.01369 8.02799L4.78103 6.79533C4.5801 6.59387 4.25373 6.5944 4.05228 6.79533C3.85082 6.99625 3.85082 7.32262 4.05228 7.52408L5.64958 9.12138C5.8505 9.32284 6.17687 9.32284 6.37833 9.12138L9.89239 5.60732Z"
              fill="#007C5E"
            />
          </g>
          <defs>
            <clipPath id="clip0_10067_5543">
              <rect width="14" height="14" fill="white" />
            </clipPath>
          </defs>
        </svg>
        <Text className="content-success spacing-m-l-1" val={window.i18next.t('pmwjs_done')} size={TextSize.XXSMALL} bold />
      </div>
    );
  };

  const getPendingPill = (): ReactElement => {
    return (
      <Pill
        background={PILL_BACKGROUND_COLOR.PREMIUM_10}
        content={{
          type: PILL_CONTENT_TYPE.HYBRID,
          text: capitalizeFirstLetter(window.i18next.t('pmwjs_pending')),
          icon: 'icon-more-h',
          isBold: true,
          textColor: 'content-premium',
          iconColor: 'content-premium',
          textClasses: 'body-xxs-bold',
        }}
        className={`${styles.completionPill} spacing-p-l-2 spacing-p-r-2`}
        size={PILL_SIZE.SMALL}
      />
    );
  };

  const getOptionalPill = (): ReactElement => {
    return (
      <Pill
        background={PILL_BACKGROUND_COLOR.NEUTRAL_3}
        content={{
          type: PILL_CONTENT_TYPE.TEXT,
          text: capitalizeFirstLetter(window.i18next.t('pmwjs_optional')),
          isBold: true,
          textColor: 'content-body',
          textClasses: 'body-xxs-bold',
        }}
        className={`${styles.completionPill} spacing-p-l-2 spacing-p-r-2`}
        size={PILL_SIZE.SMALL}
      />
    );
  };

  const getAccordionHeader = (text: string, isOpen = false, completionType: SectionCompleteType = SectionCompleteType.OPTIONAL): ReactNode => {
    let pill = getOptionalPill();

    if (completionType === SectionCompleteType.DONE) {
      pill = getDonePill();
    } else if (completionType === SectionCompleteType.PENDING) {
      pill = getPendingPill();
    }

    return (
      <div className="flex-row-align-center justify-content-between _full-width">
        <div className="flex-row-align-center">
          <span className="body-xs-bold spacing-m-r-1">{text}</span>
          <Icon icon={`${styles.headerIcon} ${isOpen ? styles.open : ''} icon-caret-down`} size={IconSize.SIZE_ICON_16} />
        </div>
        {pill}
      </div>
    );
  };

  return (
    <>
      {!isSmallScreen ? <h4 className="heading-h4 spacing-p-0 content-body spacing-m-b-4">{window.i18next.t('pmwjs_event_details')}</h4> : undefined}

      <ControlledAccordion
        header={getAccordionHeader(
          window.i18next.t('pmwjs_basic_info'),
          openAccordInd === SectionType.BASIC,
          isBasicSectionComplete(title, venue, startDateTime, endDateTime, timezone) ? SectionCompleteType.DONE : SectionCompleteType.PENDING,
        )}
        {...baseAccordionParams}
        headerClassName={`${baseAccordionParams.headerClassName} ${openAccordInd === SectionType.BASIC ? styles.borderless : ''}`}
        isOpen={openAccordInd === SectionType.BASIC}
        onHeaderClick={(isOpen) => {
          updateExpandedSection(isOpen, SectionType.BASIC);
        }}
      >
        <div className="spacing-p-t-4">
          <EventWizardDetails isForReviewPage />
        </div>
      </ControlledAccordion>

      <ControlledAccordion
        header={getAccordionHeader(
          window.i18next.t('pmwjs_details'),
          openAccordInd === SectionType.MEDIA,
          isMediaSectionComplete(description, coverPhoto) ? SectionCompleteType.DONE : SectionCompleteType.PENDING,
        )}
        {...baseAccordionParams}
        headerClassName={`${baseAccordionParams.headerClassName} ${openAccordInd === SectionType.MEDIA ? styles.borderless : ''}`}
        isOpen={openAccordInd === SectionType.MEDIA}
        onHeaderClick={(isOpen) => {
          updateExpandedSection(isOpen, SectionType.MEDIA);
        }}
      >
        <div className="spacing-p-t-4">
          <EventWizardMedia />
        </div>
      </ControlledAccordion>

      <ControlledAccordion
        header={getAccordionHeader(
          window.i18next.t('pmwjs_organizer'),
          openAccordInd === SectionType.ORGANISER,
          isOrganiserSectionComplete(organisers.selectedOrganiser) ? SectionCompleteType.DONE : SectionCompleteType.PENDING,
        )}
        {...baseAccordionParams}
        headerClassName={`${baseAccordionParams.headerClassName} ${openAccordInd === SectionType.ORGANISER ? styles.borderless : ''}`}
        isOpen={openAccordInd === SectionType.ORGANISER}
        onHeaderClick={(isOpen) => {
          updateExpandedSection(isOpen, SectionType.ORGANISER);
        }}
      >
        <div className="spacing-p-t-4">
          <EventWizardOrganisers />
        </div>
      </ControlledAccordion>

      <ControlledAccordion
        header={getAccordionHeader(window.i18next.t('pmwjs_guest_information'), openAccordInd === SectionType.REGISTRATION)}
        {...baseAccordionParams}
        headerClassName={`${baseAccordionParams.headerClassName} ${openAccordInd === SectionType.REGISTRATION ? styles.borderless : ''}`}
        isOpen={openAccordInd === SectionType.REGISTRATION}
        onHeaderClick={(isOpen) => {
          updateExpandedSection(isOpen, SectionType.REGISTRATION);
        }}
      >
        <div className="spacing-p-t-4">
          <EventWizardRegistrations />
        </div>
      </ControlledAccordion>

      <ControlledAccordion
        header={getAccordionHeader(window.i18next.t('pmwjs_other'), openAccordInd === SectionType.OTHERS)}
        {...baseAccordionParams}
        headerClassName={`${baseAccordionParams.headerClassName} ${openAccordInd === SectionType.OTHERS ? styles.borderless : ''}`}
        isOpen={openAccordInd === SectionType.OTHERS}
        onHeaderClick={(isOpen) => {
          updateExpandedSection(isOpen, SectionType.OTHERS);
        }}
      >
        <div className="spacing-p-t-4">
          <EventWizardOtherOptions />
        </div>
      </ControlledAccordion>
    </>
  );
}
